import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  isPongMessage,
  PingMessage,
  PongMessage,
} from '@lakecarmel/emr-chrome-extension-common';

import { useAppDispatch } from '../state-manager/store';
import { setExtensionStatusEmrAction } from '../state-manager/slices/emrSlice';
import { isPatientSelector } from '../state-manager/selectors/appSelectors';
import {
  getChromeRuntime,
  rescanExtensionId,
  waitForExtensionId,
} from '../services/extensionId';
import { waitForParentTargetOrigin } from '../utils/parentOrigin';
import { trackPortalEvent } from '../observability/rum/rumClient';
import { PORTAL_EVENTS } from '../observability/rum/rumEvents';

const HANDSHAKE_TIMEOUT_MS = 1500;

// One ping can transiently fail even when the extension is healthy: an MV3
// service worker that is asleep on the first message, a cold start, or a brief
// window right after the extension was installed/updated where an already-open
// tab hasn't reconnected to the new registration yet. Rather than latch
// "not connected" on the first miss, we retry a few times with backoff before
// giving up — and re-ping whenever the tab regains focus, so a tab left open
// across an extension update recovers on its own without a manual reload.
const HANDSHAKE_RETRY_DELAYS_MS = [500, 1500, 3000];

function makeCorrelationId(): string {
  return `ping-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function dispatchConnected(
  dispatch: ReturnType<typeof useAppDispatch>,
  pong: PongMessage,
): void {
  dispatch(
    setExtensionStatusEmrAction({
      extensionStatus: {
        state: 'connected',
        extensionVersion: pong.extensionVersion,
        supportedActions: pong.supportedActions,
      },
    }),
  );
}

function dispatchNotInstalled(dispatch: ReturnType<typeof useAppDispatch>): void {
  dispatch(
    setExtensionStatusEmrAction({
      extensionStatus: { state: 'notInstalled' },
    }),
  );
}

// A single handshake attempt. Resolves with the pong on success, or null on
// any failure (no id, no runtime, timeout, lastError, non-pong reply). Never
// dispatches — the caller's retry loop owns the connected/notInstalled
// decision so a transient miss doesn't flip the UI to "not connected".

// A single handshake attempt. `rescan` (used by redetect / focus re-ping) asks
// for a fresh extension id instead of the cached one — only the chrome-runtime
// path acts on it; the iframe path ignores it (its parent-origin resolver
// already re-reads the id each call).
type HandshakeAttempt = (rescan: boolean) => Promise<PongMessage | null>;

const attemptPostMessageHandshake: HandshakeAttempt = () => {
  return new Promise((resolve) => {
    const correlationId = makeCorrelationId();
    let done = false;
    let timeoutHandle = 0;
    let cleanup = () => {};

    const finish = (pong: PongMessage | null) => {
      if (done) {
        return;
      }
      done = true;
      cleanup();
      resolve(pong);
    };

    const handleMessage = (event: MessageEvent) => {
      if (!isPongMessage(event.data) || event.data.correlationId !== correlationId) {
        return;
      }
      finish(event.data);
    };

    window.addEventListener('message', handleMessage);
    timeoutHandle = window.setTimeout(() => finish(null), HANDSHAKE_TIMEOUT_MS);
    cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(timeoutHandle);
    };

    const ping: PingMessage = {
      type: 'ping',
      correlationId,
      portalOrigin: window.location.origin,
    };
    // Wait for the extension's bridge content script to stamp the id, then
    // pin the postMessage target to chrome-extension://<id>. If the id never
    // arrives (the parent isn't our extension), the attempt times out → null.
    waitForParentTargetOrigin().then((target) => {
      if (done || !target) {
        return;
      }
      window.parent.postMessage(ping, target);
    });
  });
};

// `rescan` (used by the on-demand redetect and focus re-ping): re-poke the
// bridge for a FRESH id instead of trusting the cached attribute, so a tab left
// open across an extension update discards a stale id. Initial mount passes
// false — the declarative bridge has just stamped, no need to poke.
const attemptChromeRuntimeHandshake: HandshakeAttempt = (rescan) => {
  return new Promise((resolve) => {
    let done = false;
    const finish = (pong: PongMessage | null) => {
      if (done) {
        return;
      }
      done = true;
      resolve(pong);
    };

    const idPromise = rescan ? rescanExtensionId() : waitForExtensionId();
    idPromise.then((extensionId) => {
      const runtime = getChromeRuntime();
      if (!extensionId || !runtime?.sendMessage) {
        finish(null);
        return;
      }

      const ping: PingMessage = {
        type: 'ping',
        correlationId: makeCorrelationId(),
        portalOrigin: window.location.origin,
      };

      const timer = window.setTimeout(() => finish(null), HANDSHAKE_TIMEOUT_MS);

      runtime.sendMessage(extensionId, ping, (response: unknown) => {
        window.clearTimeout(timer);
        const lastError = runtime.lastError;
        finish(lastError || !isPongMessage(response) ? null : response);
      });
    });
  });
};

// Pings the chrome extension and stores the resulting status. Two transports
// are supported:
//
//   1. window.parent.postMessage — legacy path used when the portal is
//      embedded inside the extension's side-panel iframe.
//
//   2. chrome.runtime.sendMessage(extensionId, …) — direct path from a regular
//      browser tab to the extension's background service worker. Requires the
//      page origin to be in manifest.externally_connectable.matches AND the
//      extension's percix-portal-bridge content script to have stamped the id
//      onto `<html data-percix-extension-id="…">`.
//
// Each attempt is retried a few times with backoff, and re-run when the tab
// regains focus, before falling back to notInstalled — so a transient miss
// (asleep service worker, cold start, post-update reconnect) self-heals.

// Module-level trigger the mounted hook registers, so any component can force
// an on-demand re-detect (e.g. the clinician clicks New Note / Edit). null when
// the hook isn't mounted (patient view, or before App mounts). See
// redetectEmrExtension below.
let activeRedetect: (() => void) | null = null;

// Force an immediate re-detect of the extension, discarding any stale cached
// id (the attempt runs in `rescan` mode). Safe to call anytime — a no-op when
// the hook isn't mounted. Used at the moments the clinician acts on a note so a
// tab left open across an extension update reconnects without a manual reload.
export function redetectEmrExtension(): void {
  activeRedetect?.();
}

// Re-detect no more often than this (focus churn / rapid clicks shouldn't spam
// the service worker). A re-ping that still succeeds is a cheap no-op.
const REDETECT_THROTTLE_MS = 5000;

export function useEmrExtension(): void {
  const dispatch = useAppDispatch();
  const isPatient = useSelector(isPatientSelector);

  useEffect(() => {
    // EMR is a clinician-only workflow. Patients must never trigger the
    // extension handshake or subscribe to extension messages — bail before any
    // ping is posted or listener is registered.
    if (isPatient) {
      return;
    }

    const inIframe = window.parent !== window;
    const attempt = inIframe ? attemptPostMessageHandshake : attemptChromeRuntimeHandshake;

    let cancelled = false;
    let retryTimer: number | undefined;
    let lastRedetectAt = 0;

    // Run the attempt sequence: try once, and on failure schedule the next
    // retry from HANDSHAKE_RETRY_DELAYS_MS. Dispatches connected on the first
    // success, or notInstalled only after every attempt has missed. `rescan`
    // (true for the on-demand redetect / focus re-ping) re-resolves a fresh id
    // so a tab holding a stale id from a prior extension version recovers.
    const runSequence = (attemptIndex: number, rescan: boolean) => {
      attempt(rescan).then((pong) => {
        if (cancelled) {
          return;
        }
        if (pong) {
          dispatchConnected(dispatch, pong);
          trackPortalEvent(PORTAL_EVENTS.EMR_HANDSHAKE_SUCCEEDED, {
            outcome: 'success',
            extensionVersion: pong.extensionVersion,
            retryCount: attemptIndex,
          });
          return;
        }
        if (attemptIndex < HANDSHAKE_RETRY_DELAYS_MS.length) {
          retryTimer = window.setTimeout(
            () => runSequence(attemptIndex + 1, rescan),
            HANDSHAKE_RETRY_DELAYS_MS[attemptIndex],
          );
          return;
        }
        dispatchNotInstalled(dispatch);
        trackPortalEvent(PORTAL_EVENTS.EMR_EXTENSION_UNREACHABLE, {
          outcome: 'failed',
          normalizedErrorCode: 'EMR_EXTENSION_UNREACHABLE',
          retryCount: HANDSHAKE_RETRY_DELAYS_MS.length,
        });
      });
    };

    // Initial detect: the declarative bridge just stamped, so no rescan needed.
    runSequence(0, false);

    // On-demand / focus re-detect. Re-runs even AFTER a prior success so a tab
    // whose extension updated under it (breaking the connection) recovers — the
    // old code stopped once `connected`, which couldn't heal a post-connect
    // break. Throttled, and always in `rescan` mode to refresh a stale id.
    const reDetect = () => {
      if (cancelled) {
        return;
      }
      const now = Date.now();
      if (now - lastRedetectAt < REDETECT_THROTTLE_MS) {
        return;
      }
      lastRedetectAt = now;
      window.clearTimeout(retryTimer);
      runSequence(0, true);
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      reDetect();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    activeRedetect = reDetect;

    return () => {
      cancelled = true;
      window.clearTimeout(retryTimer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      if (activeRedetect === reDetect) {
        activeRedetect = null;
      }
    };
  }, [dispatch, isPatient]);
}
